import { MediumSmallTitle } from '@/components/investors/shared';
import { SeoProps } from '@/components/shared/SEO';
import { InvestorsPageTemplate } from '@/pageTemplate/InvestorsPageTemplage';
import daniel from '@/static/assets/founders_letter/daniel.jpg';
import shai from '@/static/assets/founders_letter/shai.png';
import { Flex, font } from '@lemonade-hq/cdk';
import Image from 'next/image';
import styled from 'styled-components';

const Content = styled.p`
  ${font('main', { fontSize: '1.6rem', lineHeight: '145%' })};
`;

const List = styled.ul`
  list-style-type: disc;
  margin-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const MAX_WIDTH = 600;

const seo = {
  title: 'Our Lemonade Stand',
  description:
    'Read the latest Founders’ Letter for insights into Lemonade’s vision, strategy, and reflections from our leadership team for shareholders.',
} as SeoProps;

export default function FoundersLetterPage() {
  return (
    <InvestorsPageTemplate seo={seo}>
      <Flex flexDirection="column" width="100%" maxWidth={MAX_WIDTH} gap="20px" px={[20, 20, 0]} as="article">
        <Flex flexDirection="column" gap="2px" mb={10}>
          <MediumSmallTitle>
            A letter from our cofounders{' '}
            <a href="https://x.com/daschreiber" target="_blank" rel="noopener noreferrer">
              Daniel Schreiber
            </a>{' '}
            and{' '}
            <a href="https://x.com/shai_wininger" target="_blank" rel="noopener noreferrer">
              Shai Wininger
            </a>
          </MediumSmallTitle>
        </Flex>
        <Content>
          Since the dawn of time, insurance has both propelled progress and been revolutionized by it. Early humans
          risk-pooled intuitively, earning a claim on communal provisions in bad times by sharing their bounty in good
          ones. The Agricultural Revolution transformed risk pooling from an adaptive instinct to a facet of trade, with
          ‘assurance’ included in loan agreements throughout antiquity. A slew of inventions in the 14th century
          triggered the Commercial Revolution, which saw the ousting of lenders from insurance, and the inauguration of
          the first insurance companies. These thrived until the Scientific Revolution, when the discovery of
          probability theory signaled the toppling of every insurance company that predated it, and the emergence of the
          insurance dynasties that have reigned ever since
        </Content>
        <Content>A new revolution now threatens these hegemons.</Content>
        <Content>
          The World Economic Forum labeled the transformations we are experiencing today the Fourth Industrial
          Revolution, and with good reason. The pace and breadth of today’s innovations have no historical precedent,
          and they are spreading at an exponential, rather than linear, pace. As transformative as the prior revolutions
          were for insurance, there is reason to believe that today’s will be even more so. No part of the value chain
          is immune this time: distribution models, business models, statistical tools, systems of management, cost
          structures, corporate structures, corporate culture, technology stacks, user experience, marketing channels,
          data sources, data uses, value propositions, human capital — all these and more are being upended.
        </Content>
        <Content>Which is why we founded Lemonade.</Content>
        <Content>
          We wouldn’t know how to shepherd incumbents through these momentous changes; nor do we know of a technological
          solution to their innovator’s dilemma. Our analyses led us to conclude that a new kind of insurance company,
          built from scratch on an unconflicted business model and cutting-edge technology, will enjoy structural
          advantages that will manifest ever more powerfully over time.
        </Content>
        <Content>Lemonade aspires to be such a company.</Content>
        <List>
          <li>
            <b>We recognize this is an audacious undertaking, and the journey will be long and bumpy.</b> We are
            energized by the David vs. Goliath dynamic, and enjoy making things from scratch: brands, experiences,
            technologies, products, cultures, organizations. But Lemonade is not everyone’s cup of tea, which is why we
            wanted to outline our approach, in the hope that investors who share our thinking will be drawn to Lemonade,
            while those who do not will seek their fortunes elsewhere.
          </li>
          <li>
            <b>We are committed to planning, but not to plans.</b> Uncharted territory is just that: we have
            state-of-the-art kit, an intrepid team and an ambitious destination, but no map. So we explore, probe, and
            course correct. We think strategically, but are opportunistically opportunistic. Our squads hold daily
            ‘stand-up’ meetings, run weekly sprints and measure themselves against quarterly objectives, which in turn
            reflect our annual targets. These concentric planning cycles keep us on track, but also enable us to change
            plans from one day to the next. When opportunities present, or competitors react, or new data emerge, the
            tasks doled out at ‘stand-up’ the next morning adjust accordingly. That’s the plan.
          </li>

          <li>
            <b>We are short term patient, but long term greedy.</b> We believe opportunities such as ours are rare and
            fleeting. Industries like insurance are reinvented once every few centuries. Optimizing for profitability is
            important, but can wait; we aim to first grow fast and capture as much market share, mind share, and as
            large a <a href="https://www.lemonade.com/lemonade-goes-global">geographical footprint</a> as possible. If
            we were to reverse that, we would have a finely honed business, but would risk losing the market. That does
            not mean our bottom line does not guide or constrain us — it absolutely should and does. We do not launch
            products, open territories, or sell policies we believe will be a long-term drain, rather than a long-term
            gain. But the key phrase is &ldquo;long term.&rdquo;
          </li>

          <li>
            <b>We are risk takers, not thrill seekers.</b> We endeavor to be driven by data, not emotions. But data are
            invariably incomplete, and while waiting for more data decreases error rates, it also blunts potential
            upside. We prefer to make decisions under conditions of uncertainty, and to abandon bad bets as soon as the
            data reveal them to be so. That translates into greater volatility, but also to better aggregate returns.
            It’s a trade we are comfortable making.
          </li>

          <li>
            <b>We are transparent, except when we are not.</b> We will explain why we zig or zag, and be forthright
            about our <a href="https://www.lemonade.com/transparency">past mistakes and future plans,</a> except when
            revealing that information might hurt the business and its disclosure is not required by law. By disposition
            we are transparent, and default to sharing more rather than less. But we know that transparency is subject
            to diminishing returns, and at some point, negative returns. We try to be guided by that.
          </li>

          <li>
            <b>We see our job as value creation, not share price maximization.</b> We are not interested in our share
            price on a day to day, week to week or month to month basis. On these timescales share prices fluctuate for
            a myriad of reasons, some correlated with long term value creation, others uncorrelated, yet others
            inversely correlated. Success will reflect itself in our share price in the fullness of time, but short-term
            price flutters are noise, and we will not credit them as signals.
          </li>

          <li>
            <b>We believe values add value.</b> Lemonade is a{' '}
            <a href="https://www.lemonade.com/blog/worlds-public-benefit-insurance-company/">
              Public Benefit Corporation,
            </a>{' '}
            meaning that while we are about profit maximization over the long run, that is not all we are about. Our
            business decisions consider the greater good, and the value we are looking to create is measured in many
            currencies, money being but one of them. In any event, we reject the dichotomy between doing well and doing
            right. Ours is a world of increasing abundance, with consumers climbing ever higher on Maslow’s hierarchy of
            needs. Lemonade users, by definition, already have a roof over their heads, so even as we protect their
            homes, we also want to enrich their sense of community, esteem, and self-actualization. These transform a
            transactional relationship into a meaningful one, a rarity in insurance. Increasingly, we believe, brands
            that exist narrowly for profit maximization will not even achieve that. In the 21st century, creating value
            requires a concomitant <a href="https://www.lemonade.com/giveback">commitment to values.</a>
          </li>
        </List>
        <Content>
          The contest to be the preeminent insurance brand of the next century is just getting started, and for the
          first time in a long time, the title seems up for grabs. We have assembled a deft team, we are armed with the
          latest tech, and the winds of change are at our backs. None of these guarantee success, of course, and our
          competitors are entrenched and truly formidable. But we like our chances. After all, such has been the story
          of insurance since the dawn of time.
        </Content>

        <Flex alignItems="center" gap="10px" mt={30}>
          <Image src={daniel} alt="Daniel" width={165} height={52} />
          <Image src={shai} alt="Shai" width={165} height={63} />
        </Flex>

        <Content>Daniel & Shai</Content>
      </Flex>
    </InvestorsPageTemplate>
  );
}
